import React, { useContext, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Checkbox, FormControlLabel, Grid, LinearProgress, MenuItem, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import * as Sentry from '@sentry/react';

import { UserContext } from '@/components/App';
import NavResponsiveModal, {
    ModalActions,
    ModalContent,
    ModalHeader,
} from '@/components/Navigation/NavResponsiveModal';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { CREATE_CORP_CLIENT } from '@/constants/apiRoutes';
import { post } from '@/utilities/onwardClient';
import { toE164, toNational, validate as validatePhone } from '@/utilities/formatPhoneNumber';

import { CorpAdminContext } from '../store';

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || ''));

const buildInitialValues = (config) => {
    const init = {};
    config.fields.forEach((field) => {
        if (field.type === 'multiselect') {
            init[field.key] = [];
        } else {
            init[field.key] = field.default ?? '';
        }
    });
    return init;
};

const CreateClientModal = ({ open, onClose, config }) => {
    const { user, refetchAdminClients } = useContext(UserContext);
    const { callbacks } = useContext(CorpAdminContext);
    const { setNotification } = callbacks;

    const [values, setValues] = useState(() => buildInitialValues(config));
    const [billingSame, setBillingSame] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setValues(buildInitialValues(config));
            setBillingSame(false);
        }
    }, [open, config]);

    const billingMirrors = useMemo(
        () => config.fields.filter((f) => f.billingMirror).map((f) => ({ from: f.billingMirror, to: f.key })),
        [config]
    );
    const mirrorSourceSnapshot = billingMirrors.map((m) => values[m.from] || '').join(' ');

    useEffect(() => {
        if (!billingSame) return;
        setValues((prev) => {
            const next = { ...prev };
            billingMirrors.forEach(({ from, to }) => {
                next[to] = prev[from] || '';
            });
            return next;
        });
    }, [billingSame, billingMirrors, mirrorSourceSnapshot]);

    const setField = (key) => (e) => setValues((prev) => ({ ...prev, [key]: e.target.value }));
    const setMultiField = (key) => (_e, items) => setValues((prev) => ({ ...prev, [key]: items.map((i) => i.value) }));

    const requiredKeys = useMemo(() => config.fields.filter((f) => f.required).map((f) => f.key), [config]);

    const valid = useMemo(() => {
        if (requiredKeys.some((k) => (Array.isArray(values[k]) ? values[k].length === 0 : !values[k]))) return false;
        const emailFields = config.fields.filter((f) => f.type === 'email');
        if (!emailFields.every((f) => !values[f.key] || isValidEmail(values[f.key]))) return false;
        const phoneFields = config.fields.filter((f) => f.type === 'phone');
        return phoneFields.every((f) => !values[f.key] || validatePhone(values[f.key]));
    }, [requiredKeys, values, config]);

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const phoneKeys = config.fields.filter((f) => f.type === 'phone').map((f) => f.key);
            const phoneValuesE164 = phoneKeys.reduce((acc, key) => {
                if (values[key] && validatePhone(values[key])) acc[key] = toE164(values[key]);
                return acc;
            }, {});

            const payload = {
                role: config.role,
                organization_id: user?.organization_id || null,
                ...values,
                ...phoneValuesE164,
                ...(config.staticFields || {}),
            };
            await post(CREATE_CORP_CLIENT, payload);
            if (refetchAdminClients) await refetchAdminClients();
            setNotification?.({ severity: 'success', message: `${config.title} created` });
            onClose();
        } catch (err) {
            Sentry.captureException(err);
            console.error(err);
            setNotification?.({ severity: 'error', message: `Error creating ${config.role.toLowerCase()}` });
        } finally {
            setSubmitting(false);
        }
    };

    const renderField = (field) => {
        const { key, label, type, options, required, fullWidth, billingMirror, colSpan } = field;
        const colProps = fullWidth
            ? { xs: 12 }
            : colSpan
            ? { xs: 12, sm: colSpan, md: colSpan }
            : { xs: 12, sm: 6, md: 4 };
        const disabled = !!(billingMirror && billingSame);

        if (type === 'multiselect') {
            const selected = (values[key] || []).map((v) => options.find((o) => o.value === v)).filter(Boolean);
            return (
                <Grid item key={key} {...colProps}>
                    <Autocomplete
                        multiple
                        options={options}
                        getOptionLabel={(opt) => opt.label}
                        getOptionSelected={(opt, val) => opt.value === val.value}
                        value={selected}
                        onChange={setMultiField(key)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label={label}
                                required={required}
                                InputProps={{
                                    ...params.InputProps,
                                    style: {
                                        ...params.InputProps?.style,
                                        flexWrap: 'nowrap',
                                        overflow: 'hidden',
                                    },
                                }}
                            />
                        )}
                    />
                </Grid>
            );
        }

        if (type === 'select') {
            return (
                <Grid item key={key} {...colProps}>
                    <TextField
                        select
                        variant="outlined"
                        fullWidth
                        label={label}
                        required={required}
                        disabled={disabled}
                        value={values[key] || ''}
                        onChange={setField(key)}
                    >
                        {options.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
            );
        }

        const isEmail = type === 'email';
        const isPhone = type === 'phone';
        const hasEmailError = isEmail && !!values[key] && !isValidEmail(values[key]);
        const hasPhoneError = isPhone && !!values[key] && !validatePhone(values[key]);

        const onBlur = isPhone
            ? () => {
                  const raw = values[key];
                  if (raw && validatePhone(raw)) {
                      setValues((prev) => ({ ...prev, [key]: toNational(raw) }));
                  }
              }
            : undefined;

        return (
            <Grid item key={key} {...colProps}>
                <TextField
                    variant="outlined"
                    fullWidth
                    type={isEmail ? 'email' : isPhone ? 'tel' : 'text'}
                    label={label}
                    required={required}
                    disabled={disabled}
                    error={hasEmailError || hasPhoneError}
                    helperText={
                        hasEmailError ? 'Enter a valid email' : hasPhoneError ? 'Enter a valid phone number' : undefined
                    }
                    value={values[key] || ''}
                    onChange={setField(key)}
                    onBlur={onBlur}
                />
            </Grid>
        );
    };

    const fieldNodes = [];
    let billingToggleEmitted = false;
    config.fields.forEach((field) => {
        if (field.billingMirror && !billingToggleEmitted) {
            fieldNodes.push(
                <Grid item xs={12} key="__billing_toggle">
                    <FormControlLabel
                        control={
                            <Checkbox
                                color="primary"
                                checked={billingSame}
                                onChange={(e) => setBillingSame(e.target.checked)}
                            />
                        }
                        label="Billing address same as primary"
                    />
                </Grid>
            );
            billingToggleEmitted = true;
        }
        fieldNodes.push(renderField(field));
    });

    return (
        <NavResponsiveModal open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <ModalHeader
                title={config.title}
                onClose={onClose}
                css={css`
                    border-bottom: 1px solid lightgray;
                `}
            />
            {submitting && (
                <LinearProgress
                    color="primary"
                    css={css`
                        width: 100%;
                    `}
                />
            )}
            <ModalContent>
                <Grid container spacing={2}>
                    {fieldNodes}
                </Grid>
            </ModalContent>
            <ModalActions>
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                <PrimaryButton disabled={!valid || submitting} onClick={handleSubmit}>
                    Create
                </PrimaryButton>
            </ModalActions>
        </NavResponsiveModal>
    );
};

export default CreateClientModal;
